import type { DataTableFilterField } from "@/types";
import {
  type ColumnDef,
  type ColumnFiltersState,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  type PaginationState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from "@tanstack/react-table";
import * as React from "react";
import { z } from "zod";

import { useRouter, useRouterState, useSearch } from "@tanstack/react-router";

interface UseDataTableProps<TData, TValue> {
  /**
   * The data for the table.
   * @default []
   * @type TData[]
   */
  data: TData[];

  /**
   * The columns of the table.
   * @default []
   * @type ColumnDef<TData, TValue>[]
   */
  columns: ColumnDef<TData, TValue>[];

  /**
   * The number of pages in the table.
   * @type number
   */
  pageCount: number;

  /**
   * The default number of rows per page.
   * @default 10
   * @type number | undefined
   * @example 20
   */
  defaultPerPage?: number;

  /**
   * The default sort order.
   * @default undefined
   * @type `${Extract<keyof TData, string | number>}.${"asc" | "desc"}` | undefined
   * @example "createdAt.desc"
   */
  defaultSort?: `${Extract<keyof TData, string | number>}.${"asc" | "desc"}`;

  /**
   * Defines filter fields for the table. Supports both dynamic faceted filters and search filters.
   * - Faceted filters are rendered when `options` are provided for a filter field.
   * - Otherwise, search filters are rendered.
   *
   * The indie filter field `value` represents the corresponding column name in the database table.
   * @default []
   * @type { label: string, value: keyof TData, placeholder?: string, options?: { label: string, value: string, icon?: React.ComponentType<{ className?: string }> }[] }[]
   * @example
   * ```ts
   * // Render a search filter
   * const filterFields = [
   *   { label: "Title", value: "title", placeholder: "Search titles" }
   * ];
   * // Render a faceted filter
   * const filterFields = [
   *   {
   *     label: "Status",
   *     value: "status",
   *     options: [
   *       { label: "Todo", value: "todo" },
   *       { label: "In Progress", value: "in-progress" },
   *       { label: "Done", value: "done" },
   *       { label: "Canceled", value: "canceled" }
   *     ]
   *   }
   * ];
   * ```
   */
  filterFields?: DataTableFilterField<TData>[];

  disablePagination?: boolean;
  enableRowSelection?: boolean;
}

const schema = z.object({
  page: z.coerce.number().default(1),
  per_page: z.coerce.number().optional(),
  sort: z.string().optional(),
});

export function useDataTable<TData, TValue>({
  data,
  columns,
  pageCount,
  defaultPerPage = 10,
  defaultSort,
  filterFields = [],
  disablePagination,
}: UseDataTableProps<TData, TValue>) {
  const router = useRouter();
  const { location } = useRouterState();
  const pathname = location.pathname;
  const searchParams = useSearch({ strict: false }) as Record<string, string>;

  // Search params
  const search = schema.parse(searchParams);
  const page = search.page;
  const sort = search.sort ?? defaultSort;
  const [column, order] = sort?.split(".") ?? [];
  const [sorting, setSorting] = React.useState<SortingState>(() => [
    column
      ? {
          id: column,
          desc: order === "desc",
        }
      : {
          id: "",
          desc: false,
        },
  ]);

  // Create query string
  const createQueryString = React.useCallback(
    (params: Record<string, string | number | null>) => {
      const searchEntries = Object.entries(searchParams);
      const newEntries = Object.entries(params);
      const allParams = new Map(searchEntries);

      newEntries.forEach(([key, value]) => {
        if (value === null) {
          allParams.delete(key);
        } else {
          allParams.set(key, String(value));
        }
      });

      return Array.from(allParams)
        .map(([key, value]) => `${key}=${value}`)
        .join("&");
    },
    [searchParams],
  );

  // Initialize pageSize - prioritize URL param, then defaultPerPage
  const initialPageSize = React.useMemo(() => {
    return search.per_page
      ? parseInt(search.per_page as unknown as string, 10)
      : defaultPerPage;
  }, [search.per_page, defaultPerPage]);

  // Handle server-side pagination with proper initialization
  const [{ pageIndex, pageSize }, setPagination] =
    React.useState<PaginationState>(() => ({
      pageIndex: page - 1,
      pageSize: initialPageSize,
    }));

  // Sync table state with URL params
  React.useEffect(() => {
    setPagination({
      pageIndex: page - 1,
      pageSize: initialPageSize,
    });
  }, [page, initialPageSize]);

  // Memoize pagination state to prevent unnecessary re-renders
  const pagination = React.useMemo(
    () => ({
      pageIndex,
      pageSize: initialPageSize,
    }),
    [pageIndex, initialPageSize],
  );

  // Handlers for pagination
  const handlePaginationChange = React.useCallback(
    (newPageIndex: number, newPageSize: number) => {
      if (disablePagination) return;

      setPagination({ pageIndex: newPageIndex, pageSize: newPageSize });

      router.navigate({
        to: `${pathname}?${createQueryString({
          page: newPageIndex + 1,
          per_page: newPageSize === 10 ? null : newPageSize,
        })}`,
        resetScroll: true,
      });
    },
    [disablePagination, pathname, createQueryString, router],
  );

  // Custom handlers for pagination controls
  const handlePageSizeChange = React.useCallback(
    (newPageSize: number) => {
      handlePaginationChange(0, newPageSize); // Reset to first page when changing page size
    },
    [handlePaginationChange],
  );

  const handlePageChange = React.useCallback(
    (newPageIndex: number) => {
      handlePaginationChange(newPageIndex, pageSize);
    },
    [handlePaginationChange, pageSize],
  );

  // Memoize computation of searchableColumns and filterableColumns
  const { searchableColumns, filterableColumns } = React.useMemo(() => {
    return {
      searchableColumns: filterFields.filter((field) => !field.options),
      filterableColumns: filterFields.filter((field) => field.options),
    };
  }, [filterFields]);

  // Initial column filters
  const initialColumnFilters = React.useMemo(() => {
    return Array.from(Object.entries(searchParams)).reduce<ColumnFiltersState>(
      (filters, [key, value]) => {
        const filterableColumn = filterableColumns.find(
          (column) => column.value === key,
        );
        const searchableColumn = searchableColumns.find(
          (column) => column.value === key,
        );

        if (filterableColumn) {
          filters.push({
            id: key,
            value: value.split("."),
          });
        } else if (searchableColumn) {
          filters.push({
            id: key,
            value: [value],
          });
        }

        return filters;
      },
      [],
    );
  }, [filterableColumns, searchableColumns, searchParams]);

  // Table states
  const [rowSelection, setRowSelection] = React.useState({});
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>(initialColumnFilters);

  const table = useReactTable({
    data,
    columns,
    pageCount: pageCount ?? -1,
    state: {
      pagination,
      sorting,
      columnVisibility,
      rowSelection,
      columnFilters,
    },
    enableRowSelection: true,
    onRowSelectionChange: setRowSelection,
    onPaginationChange: (updater) => {
      if (typeof updater === "function") {
        const newPagination = updater(pagination);
        handlePaginationChange(newPagination.pageIndex, newPagination.pageSize);
      } else {
        handlePaginationChange(updater.pageIndex, updater.pageSize);
      }
    },
    onSortingChange: (updater) => {
      const newSorting =
        typeof updater === "function" ? updater(sorting) : updater;
      setSorting(newSorting);
      const sortParam = newSorting[0];
      const sortKey = sortParam?.id;
      const sortDir = sortParam?.desc ? "desc" : "asc";

      router.navigate({
        to: `${pathname}?${createQueryString({
          sort: sortKey ? `${sortKey}.${sortDir}` : null,
          page: 1, // Reset to page 1 on sort change
        })}`,
        resetScroll: true,
      });
    },
    onColumnFiltersChange: setColumnFilters,
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
  });

  return {
    table,
    handlePageSizeChange,
    handlePageChange,
  };
}
